/**
 * The typed edge against the backend.
 *
 * Every shape here mirrors a Pydantic model in `models-hosting-servers/app/routes/`.
 * When one changes, this changes with it — that is the whole reason the portal is
 * TypeScript.
 */

// Always same-origin, in every environment. Production: Caddy serves this bundle
// and proxies /api, /ollama, /vllm and /v1 to the backend. Development: Vite's
// dev server proxies the same paths (see vite.config.ts).
//
// There is deliberately no base URL. A cross-origin request would need the
// session cookie sent with SameSite=None over HTTPS, and a plain-HTTP dev setup
// would drop it silently — login appearing to succeed while every following
// request came back 401. Proxying in dev avoids the whole class of problem.
export const API_BASE = "";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
  ) {
    super(detail);
    this.name = "ApiError";
  }
}

async function get<T>(path: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      headers: { Accept: "application/json" },
      credentials: "include",
    });
  } catch (cause) {
    // fetch only rejects on a network-level failure, which here means the
    // backend is unreachable rather than unhappy. Say so plainly — a blank
    // screen with "Failed to fetch" in the console is what the M1 gate forbids.
    throw new ApiError(0, `Cannot reach the server at ${API_BASE || "this origin"}.`);
  }

  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { detail?: string };
      if (body?.detail) detail = body.detail;
    } catch {
      // non-JSON error body; the status line is all we have
    }
    throw new ApiError(res.status, detail);
  }
  return (await res.json()) as T;
}

/* ---------------------------------------------------------------- hardware */

export interface Gpu {
  index: number;
  name: string;
  vram_total_mib: number | null;
  vram_used_mib: number | null;
  vram_free_mib: number | null;
  utilization_pct: number | null;
  temperature_c: number | null;
  driver_version: string | null;
}

export interface Hardware {
  gpu_available: boolean;
  gpus: Gpu[];
  cpu_logical: number | null;
  cpu_physical: number | null;
  cpu_percent: number | null;
  ram_total_mib: number | null;
  ram_used_mib: number | null;
  disk_path: string | null;
  disk_total_gib: number | null;
  disk_free_gib: number | null;
}

export const fetchHardware = () => get<Hardware>("/api/system/hardware");

/**
 * One reading per machine, and the same shape on a single-node install, where
 * it is a list of one with no node name.
 *
 * The console draws this rather than /hardware. That endpoint reports the box
 * the API runs on — which on a router is a Mac with no GPU, standing in for a
 * fleet that has one. An unreachable node is still listed, with no numbers,
 * rather than silently missing from a page someone is reading to find out
 * which machine has room.
 */
export interface NodeHardware {
  node: string | null;
  reachable: boolean;
  hardware: Hardware | null;
}

export const fetchHardwareAll = () =>
  get<NodeHardware[]>("/api/system/hardware/all");

/* ------------------------------------------------------------------ status */

export interface RuntimeStatus {
  reachable: boolean;
  version: string | null;
  url?: string | null;
  error?: string | null;
}

export interface Status {
  platform_version: string;
  ollama: RuntimeStatus;
  vllm: RuntimeStatus;
}

export const fetchStatus = () => get<Status>("/api/system/status");

/* ------------------------------------------------------------------ models */

export type ModelType = "chat" | "embedding" | "vision" | "rerank" | "unknown";

export interface Model {
  name: string;
  provider: "ollama" | "vllm";
  model_type: ModelType;
  size_bytes: number | null;
  modified_at: string | null;
  digest: string | null;
  format: string | null;
  family: string | null;
  parameter_size: string | null;
  quantization: string | null;
  /** The architecture's ceiling — the same for a base model and anything
   *  derived from it, which is why it cannot be shown on its own. */
  context_length: number | null;
  /** What this model is actually set to. Null when nothing was baked in, so
   *  the ceiling is what it gives you. */
  num_ctx: number | null;
  embedding_length: number | null;
  capabilities: string[];
  /** Which machine holds it. Null on a single-machine install, where the
   *  question does not arise. */
  node: string | null;
  loaded: boolean | null;
}

export interface RunningModel {
  name: string;
  provider: "ollama" | "vllm";
  node: string | null;
  vram_bytes: number | null;
  expires_at: string | null;
  context_length: number | null;
}

export const fetchOllamaModels = () => get<Model[]>("/ollama/models");
export const fetchOllamaRunning = () => get<RunningModel[]>("/ollama/running");

/* -------------------------------------------------------------------- jobs */

export type JobKind = "pull" | "remove" | "load" | "unload" | "configure";
export type JobState = "running" | "done" | "failed" | "cancelled";

export interface Job {
  id: string;
  kind: JobKind;
  provider: string;
  model: string;
  state: JobState;
  percent: number | null;
  detail: string | null;
  total: number | null;
  completed: number | null;
  error: string | null;
  started_at: number;
  updated_at: number;
  finished_at: number | null;
  node: string | null;
}

export const fetchJobs = () => get<Job[]>("/api/jobs?limit=20");

/** What the buttons kick off. Each returns a job id rather than waiting — a
 *  pull runs for minutes and must not be tied to this request. */
export interface Started {
  job_id: string;
  model: string;
  kind: JobKind;
  node: string | null;
}

/* Every write names a machine; every read aggregates. A router never picks
 * where a model gets installed or which GPU it is loaded into — that is a
 * decision about hardware, and only the operator knows which box they meant.
 * `node` is ignored on a single-machine install, which has one place to do
 * anything. */
export const installModel = (model: string, node?: string | null) =>
  send<Started>("POST", "/ollama/install_model", { model, node });

export const removeModel = (model: string, node?: string | null) =>
  send<Started>("DELETE", "/ollama/remove_model", { model, node });

export const loadModel = (model: string, keep_alive = "5m", node?: string | null) =>
  send<Started>("POST", "/ollama/start_model", { model, keep_alive, node });

export const unloadModel = (model: string, node?: string | null) =>
  send<Started>("POST", "/ollama/stop_model", { model, node });

export const cancelJob = (id: string) =>
  send<Job>("POST", `/api/jobs/${id}/cancel`);

/** A job is worth showing while it runs, and for a moment after it fails so the
 *  reason can be read. A job that succeeded needs no announcement — the model
 *  list changing is the announcement. */
export function isNoteworthy(job: Job): boolean {
  if (job.state === "running") return true;
  if (job.state !== "failed") return false;
  return Date.now() / 1000 - (job.finished_at ?? 0) < 120;
}

/* ----------------------------------------------------------- configuration */

/** Where a setting takes effect. Not decoration — the form groups by it and
 *  says so, because the three are not interchangeable and confusing them is how
 *  someone sets a keep-alive that Ollama accepts and then ignores. */
export type Applies = "model" | "load" | "request";

export interface SchemaField {
  key: string;
  group: string;
  label: string;
  type: "string" | "int" | "float" | "bool" | "enum";
  default: string | number | boolean | null;
  applies: Applies;
  help?: string;
  unit?: string;
  advanced?: boolean;
  presets?: number[];
  options?: { value: string; label: string }[];
  min?: number | null;
  max?: number | null;
  show_when?: { model_type?: string[]; capability?: string[] };
}

export interface SchemaGroup {
  key: string;
  label: string;
  order: number;
}

export interface ConfigSchema {
  provider: string;
  model_type: ModelType;
  /** The model's own context ceiling. null when it does not report one — which
   *  is honest, and better than a made-up maximum someone then sets against. */
  ceiling: number | null;
  groups: SchemaGroup[];
  fields: SchemaField[];
  /** Present only when a model was named: what it is set to right now, so the
   *  form opens on the truth rather than on defaults. */
  current?: Record<string, string | number | boolean | string[]>;
  capabilities?: string[];
}

/** The form is drawn from this, never hard-coded. Adding a setting later is a
 *  backend change only — which is the whole point of the endpoint existing. */
export const fetchSchema = (model?: string, node?: string | null) => {
  const query = new URLSearchParams();
  if (model) query.set("model", model);
  if (node) query.set("node", node);
  const q = query.toString();
  return get<ConfigSchema>(`/ollama/schema${q ? `?${q}` : ""}`);
};

export interface ConfigureRequest {
  model: string;
  node?: string | null;
  alias?: string | null;
  num_ctx?: number | null;
  temperature?: number | null;
  top_p?: number | null;
  top_k?: number | null;
  repeat_penalty?: number | null;
  num_predict?: number | null;
}

export const configureModel = (body: ConfigureRequest) =>
  send<Started>("POST", "/ollama/configure_model", body);

/* ------------------------------------------------------------------ nodes */

/** A machine this router sends work to.
 *
 *  Note what is absent: the token. It is write-only from the outside — set it,
 *  replace it, but it never comes back out. No screen needs it, and a value on
 *  screen is a value in a screenshot. */
export interface NodeView {
  name: string;
  url: string;
  note: string | null;
  enabled: boolean;
  /** False when this node's certificate is not checked. Surfaced so the row can
   *  be marked — a node connected without that check must never look like every
   *  other one. */
  verify_tls: boolean;
  reachable: boolean;
  error: string | null;
  model_count: number;
  loaded_count: number;
  free_vram_mib: number | null;
}

export interface NodeProbe {
  ok: boolean;
  detail: string;
  version?: string | null;
  model_count?: number;
}

export const fetchNodes = () => get<NodeView[]>("/api/nodes");

/** Tried before it is saved. Takes the typed address and token rather than a
 *  saved node — half-added machines that never worked are their own mess. */
export const testNode = (url: string, token: string, verify_tls = true) =>
  send<NodeProbe>("POST", "/api/nodes/test", { url, token, verify_tls });

export const addNode = (
  name: string,
  url: string,
  token: string,
  note?: string,
  verify_tls = true,
) => send<NodeView>("POST", "/api/nodes", { name, url, token, note, verify_tls });

/** What breaks if this machine goes. Asked before the confirm, so the warning
 *  names the actual models rather than saying "are you sure". */
export const nodeImpact = (name: string) =>
  get<{ node: string; orphaned: string[] }>(`/api/nodes/${encodeURIComponent(name)}/impact`);

export const removeNode = (name: string) =>
  send<{ removed: string; orphaned: string[] }>(
    "DELETE",
    `/api/nodes/${encodeURIComponent(name)}`,
  );

export interface Conflict {
  model: string;
  nodes: string[];
  reason: string;
}

/** Names that mean different things on different machines. Reported, never
 *  enforced — the operator usually knows why, and refusing to serve would be a
 *  worse answer than saying these two are not the same. */
export const fetchConflicts = () => get<Conflict[]>("/api/nodes/conflicts");

/* -------------------------------------------------------------------- vLLM */

/**
 * A vLLM model is a PROCESS, which is why this looks nothing like `Model`.
 *
 * Ollama installs models to disk and loads a few of them; the interesting
 * question is "is it loaded". vLLM starts one server per model on its own port,
 * so the questions are "is the process up", "on which port", and — when it is
 * not — "why did it die". Hence `state`, `port`, `pid` and `error` where the
 * Ollama shape has sizes and quantisations.
 */
export type VllmState = "starting" | "ready" | "failed" | "stopped";

export interface VllmModel {
  /** What clients call it. Often a short alias for a long Hugging Face id. */
  name: string;
  /** The Hugging Face repo actually being served. */
  model: string;
  /** What it turned out to be, established by asking the running server —
   *  not what was requested, which is usually "auto". */
  task: "generate" | "embed";
  provider: "vllm";
  port: number;
  pid: number | null;
  state: VllmState;
  /** The exact command line. Shown so a start is reproducible by hand. */
  args: string[];
  /** Only the flags the operator typed. Resent verbatim by "Start" — the rest
   *  of `args` is generated and would be duplicated. */
  extra: string[];
  /** The settings form's answers, as chosen. The form reopens on these. */
  settings: Record<string, string | number | boolean | null>;
  /** What vLLM says it ACTUALLY serves, read back from the running process.
   *  Not an echo of `settings.max_model_len` — that would be showing someone
   *  their own input back and calling it confirmation. */
  context_length: number | null;
  error: string | null;
  started_at: number | null;
  ready_at: number | null;
  /** Which machine runs it. Null on a single-machine install, where the
   *  question does not arise. */
  node: string | null;
}

export const fetchVllmModels = () => get<VllmModel[]>("/vllm/models");
export const fetchVllmRunning = () => get<VllmModel[]>("/vllm/running");

export const fetchVllmLogs = (name: string, lines = 200, node?: string | null) =>
  get<{ name: string; lines: string[] }>(
    `/vllm/logs?name=${encodeURIComponent(name)}&lines=${lines}` +
      (node ? `&node=${encodeURIComponent(node)}` : ""),
  );

export interface StartVllmRequest {
  model: string;
  name?: string | null;
  /** "auto" unless you are deliberately forcing a generative model to embed.
   *  vLLM reads the model's own config and gets this right on its own. */
  task: "auto" | "generate" | "embed";
  /** The form's answers, keyed by the schema's field keys. */
  settings?: Record<string, string | number | boolean | null>;
  /** Free text, appended after the settings and so winning over them. */
  extra_args: string[];
  /* Which machine to start it on. Required on a router, ignored on a node:
   * where a model runs is a decision about hardware, and a router never makes
   * it for you. */
  node?: string | null;
}

export const startVllmModel = (body: StartVllmRequest) =>
  send<Started>("POST", "/vllm/start_model", body);

/** New settings on a model that already exists, which means restarting it —
 *  a vLLM setting is a launch argument and nothing can change it in place.
 *  `extra_args` omitted keeps what it had; an empty array clears them. */
export const reconfigureVllmModel = (
  name: string,
  settings: Record<string, string | number | boolean | null>,
  node?: string | null,
  extra_args?: string[] | null,
) =>
  send<Started>("POST", "/vllm/reconfigure", { name, settings, node, extra_args });

export const stopVllmModel = (name: string, node?: string | null) =>
  send<Started>("POST", "/vllm/stop_model", { name, node });

/** Drops a stopped model from the list. Refused while it is live — forgetting
 *  the row would not stop the process, only lose the port and pid needed to. */
export const forgetVllmModel = (name: string, node?: string | null) =>
  send<{ removed: string }>("DELETE", "/vllm/forget", { name, node });

/* ------------------------------------------------------------ connections */

export interface EnvVar {
  name: string;
  value: string;
}

export interface Connection {
  model: string;
  provider: "ollama" | "vllm";
  model_type: ModelType;
  state: string;
  base_url: string;
  api_format: string;
  key_masked: string;
  /** Only present when asked for — the list is masked by default so a key does
   *  not end up in a screenshot of a page nobody was reading closely. */
  key: string | null;
  key_scope: string;
  env: EnvVar[];
  note: string | null;
  /** Which machine runs it. Only vLLM sets this — an Ollama card covers every
   *  node, because the router picks where a request lands. */
  node: string | null;
}

export const fetchConnections = (reveal = false) =>
  get<Connection[]>(`/api/connections${reveal ? "?reveal=true" : ""}`);

export interface TestResult {
  ok: boolean;
  ms: number | null;
  detail: string;
}

/** Run server-side, so the key never reaches JavaScript — which is the whole
 *  point of the cookie being HttpOnly. */
export const testConnection = (model: string) =>
  send<TestResult>("POST", "/api/connections/test", { model });

/** vLLM takes a model, Ollama does not. Not arbitrary: Ollama's models share
 *  one server and one key, while each vLLM is its own server with its own — so
 *  reissuing without naming one would rotate the wrong key. */
export const reissueKey = (provider: string, model?: string | null) =>
  send<{ provider: string; model: string | null; key: string; key_masked: string }>(
    "POST",
    `/api/connections/${provider}/reissue` +
      (model ? `?model=${encodeURIComponent(model)}` : ""),
  );

/* -------------------------------------------------------------------- auth */

export interface SessionState {
  signed_in: boolean;
  expires_in_hours?: number | null;
}

/** Answers 200 either way — the app asks this precisely when it does not know,
 *  so a 401 here would be indistinguishable from a real failure. */
export const fetchSession = () => get<SessionState>("/api/auth/me");

async function send<T>(
  method: "POST" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "include",
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "Cannot reach the server.");
  }
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const parsed = (await res.json()) as { detail?: string };
      if (parsed?.detail) detail = parsed.detail;
    } catch {
      /* non-JSON body; the status line is all we have */
    }
    throw new ApiError(res.status, detail);
  }
  return (await res.json()) as T;
}

export const signIn = (password: string) =>
  send<SessionState>("POST", "/api/auth/login", { password });

export const signOut = () => send<SessionState>("POST", "/api/auth/logout");

/* ------------------------------------------------------------ formatting */

export function bytes(n: number | null | undefined): string {
  if (n == null) return "—";
  const gb = n / 1024 ** 3;
  if (gb >= 1) return `${gb.toFixed(gb >= 10 ? 0 : 1)} GB`;
  return `${Math.round(n / 1024 ** 2)} MB`;
}

export function mib(n: number | null | undefined): string {
  if (n == null) return "—";
  return n >= 1024 ? `${(n / 1024).toFixed(1)} GB` : `${n} MiB`;
}

export function count(n: number | null | undefined): string {
  return n == null ? "—" : n.toLocaleString("en-US");
}

/** "unloads in 27m", or null when it is not going to unload on a timer. */
export function expiresIn(iso: string | null): string | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  // Ollama reports a year-2000-ish or far-future date for keep_alive -1.
  if (ms > 365 * 24 * 3600 * 1000) return "always loaded";
  if (ms <= 0) return "unloading";
  const mins = Math.round(ms / 60000);
  return mins >= 60 ? `unloads in ${Math.round(mins / 60)}h` : `unloads in ${mins}m`;
}

/* --------------------------------------------------------------- live data */

/**
 * What the console is allowed to refresh on its own.
 *
 * Served by the backend rather than baked into this bundle, because the cost of
 * a poll belongs to the deployment and not to the code. On one machine it is
 * free; across a LAN to a busy GPU box it is not. Both are off unless the
 * server's `.env` turns them on.
 */
export interface LiveSetting {
  enabled: boolean;
  seconds: number;
}

export interface UiSettings {
  live_hardware: LiveSetting;
  live_running: LiveSetting;
}

export const fetchUiSettings = () => get<UiSettings>("/api/system/ui");

/* -------------------------------------------------------- vLLM settings */

/**
 * The vLLM form, drawn from the backend like the Ollama one.
 *
 * One difference matters enough to be in the types: every vLLM setting is a
 * LAUNCH argument. An Ollama setting is baked into a derived model and sticks;
 * these take effect when the process starts and cannot change while it runs.
 * `note` says so, and the dialog shows it.
 */
export interface VllmField {
  key: string;
  /** The vLLM flag this maps to. Shown so the command stays readable. */
  flag: string;
  /** `value` is `--flag X`; `switch` is the flag alone, or nothing. */
  kind: "value" | "switch";
  group: string;
  label: string;
  type: "string" | "int" | "float" | "bool" | "enum";
  default: string | number | boolean | null;
  applies: "launch";
  help?: string;
  unit?: string;
  advanced?: boolean;
  presets?: number[];
  options?: { value: string; label: string }[];
  min?: number | null;
  max?: number | null;
}

export interface VllmSchema {
  provider: string;
  /** The Hugging Face id this form was drawn for. */
  model: string | null;
  model_type: ModelType;
  /** The model's own context ceiling, from its config. Presets stop here. */
  ceiling: number | null;
  /** Short facts about the model and the machine, for the top of the form:
   *  architecture, kind, ceiling, precision, whether it needs its own code. */
  facts: string[];
  groups: SchemaGroup[];
  fields: VllmField[];
  note: string;
}

/**
 * The form for ONE model on ONE machine — never a generic one.
 *
 * Both are required. The model decides the context ceiling, the precision and
 * whether it needs its own code; the machine decides whether "split across
 * GPUs" means anything. A model Hugging Face cannot describe comes back as a
 * 422 with the reason, and the page shows that instead of a form.
 */
export const fetchVllmSchema = (model: string, node?: string | null) =>
  get<VllmSchema>(
    `/vllm/schema?model=${encodeURIComponent(model)}` +
      (node ? `&node=${encodeURIComponent(node)}` : ""),
  );

export type VllmSettings = Record<string, string | number | boolean | null>;
